import React from "react";
import { useNavigate } from "react-router-dom";
import { useBanners } from "../../utils/useBanners";

/* =====================================================================
   Promotions strip
   =====================================================================
   Admin-managed posters in the "promo" placement. Whatever is live right
   now, in sort order.

   If nothing is scheduled the bundled creatives show instead, so the
   section never becomes an empty hole on the page -- and so the site
   looks the same as it did before anyone has uploaded anything.

   Every poster is cropped into a fixed box by the stylesheet. An upload
   can be any size or shape and the layout will not move; the artwork is
   what gets trimmed, never the page.
   ===================================================================== */

const FALLBACK_PROMOS = [
  {
    id: "fallback-aviator",
    src: "/assets/images/banner111.webp",
    title: "Aviator",
    sub: "Crash game",
    cta: "Open",
    to: { game: "aviator" },
  },
  {
    id: "fallback-casino",
    src: "/assets/images/banner222.webp",
    title: "Casino",
    sub: "Live and table games",
    cta: "Browse",
    to: { path: "/casinoGames", state: { list: 0 } },
  },
];

const PromoStrip = ({ onOpenAviator }) => {
  const navigate = useNavigate();
  const promos = useBanners("promo");

  const go = (link) => {
    if (!link) return;
    if (/^https?:\/\//i.test(link)) {
      window.open(link, "_blank", "noopener");
      return;
    }
    navigate(link);
  };

  // Loading: hold the space so the sections below do not jump up and then
  // back down as the artwork arrives.
  if (promos === null) {
    return (
      <div className="rb-promos">
        <span className="rb-promo rb-skeleton" />
        <span className="rb-promo rb-skeleton" />
      </div>
    );
  }

  if (promos.length) {
    return (
      <div className="rb-promos">
        {promos.map((promo) => {
          const clickable = !!promo.link;
          const Tag = clickable ? "button" : "span";
          return (
            <Tag
              key={promo.id}
              type={clickable ? "button" : undefined}
              className={clickable ? "rb-promo rb-promo--link" : "rb-promo"}
              onClick={clickable ? () => go(promo.link) : undefined}
            >
              <img
                src={promo.src}
                alt={promo.title || "Promotion"}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  // A deleted file must not leave a broken-image glyph
                  // sitting in the middle of the home page.
                  e.currentTarget.closest(".rb-promo").style.display = "none";
                }}
              />
              {promo.title ? (
                <span className="rb-promo__body">
                  <span className="rb-promo__title">{promo.title}</span>
                  {clickable ? (
                    <span className="rb-btn rb-btn--secondary rb-btn--sm">Open</span>
                  ) : null}
                </span>
              ) : null}
            </Tag>
          );
        })}
      </div>
    );
  }

  return (
    <div className="rb-promos">
      {FALLBACK_PROMOS.map((promo) => (
        <button
          key={promo.id}
          type="button"
          className="rb-promo rb-promo--link"
          onClick={() => {
            if (promo.to.game === "aviator" && onOpenAviator) onOpenAviator();
            else if (promo.to.path) navigate(promo.to.path, { state: promo.to.state });
          }}
        >
          <img src={promo.src} alt={promo.title} loading="lazy" decoding="async" />
          <span className="rb-promo__body">
            <span>
              <span className="rb-promo__title">{promo.title}</span>
              <span className="rb-promo__sub">{promo.sub}</span>
            </span>
            <span className="rb-btn rb-btn--secondary rb-btn--sm">{promo.cta}</span>
          </span>
        </button>
      ))}
    </div>
  );
};

export default PromoStrip;
